import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, ControlOutlined, PlusOutlined, PrinterOutlined} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { ConvertCertificationToSalesInvoiceModal } from '@/components/epc/ConvertCertificationToSalesInvoiceModal';
import { PrintableIpcPaymentApplication } from '@/components/epc/PrintableIpcPaymentApplication';
import { EpcContractCockpit } from '@/components/epc/commercial-ux/EpcContractCockpit';
import {
  EpcContractEstimateBasisCard,
  EpcMilestonesEditor,
  EpcOpeningPositionForm,
  EpcSovEditor,
} from '@/components/epc/commercial-ux/EpcContractContentPanels';
import { EpcAdvancePaymentCard } from '@/components/epc/commercial-ux/EpcAdvancePaymentCard';
import {
  CommercialModelExplainer,
  CommercialModelGuideLink,
} from '@/components/epc/commercial-ux/EpcCommercialModelGuide';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import {
  commercialModelInfo,
  EPC_CONTRACT_INTERNAL_COPY_PERMISSIONS,
  normalizeCommercialModel,
} from '@/constants/epcCommercialModels';
import { useAuthStore } from '@/store/authStore';
import { epcContractAdminService } from '@/services/epcContractAdmin.service';
import { openPmvPrintOverlay } from '@/utils/openPmvPrintOverlay';
import { projectService } from '@/services/project.service';
import { isEpcCaEntityType } from '@/pages/epc/commercial/epcCaDeepLinks';

const { Title, Text } = Typography;

function SimpleTable({
  rows,
  columns,
  highlightId,
}: {
  rows: Record<string, unknown>[];
  columns: {
    title: string;
    dataIndex: string;
    width?: number;
    render?: (v: unknown, r: Record<string, unknown>) => React.ReactNode;
  }[];
  highlightId?: string;
}) {
  if (!rows.length) return <Empty description="No records" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <Table
      size="small"
      rowKey={(r) => String(r.id || JSON.stringify(r))}
      dataSource={rows}
      pagination={false}
      scroll={{ x: true }}
      rowClassName={(r) =>
        highlightId && String(r.id) === highlightId ? 'ant-table-row-selected' : ''
      }
      columns={columns.map((c) => ({
        ...c,
        render: c.render || ((v: unknown) => (v == null || v === '' ? '—' : String(v))),
      }))}
    />
  );
}

function money(v: unknown, currency?: unknown) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  const cur = currency ? `${currency} ` : '';
  return `${cur}${n.toLocaleString()}`;
}

function severityColor(s: unknown) {
  const v = String(s || '').toUpperCase();
  if (v === 'CRITICAL') return 'red';
  if (v === 'WARNING') return 'orange';
  return 'blue';
}

const EpcContractAdminWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const caTab = searchParams.get('caTab') || 'overview';
  const docId = searchParams.get('docId') || '';
  const focusEntityType = searchParams.get('entityType') || '';
  const [convertCertId, setConvertCertId] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);
  const canInternalCopy =
    !!user?.is_super_admin || EPC_CONTRACT_INTERNAL_COPY_PERMISSIONS.some((p) => user?.permissions?.includes(p));
  const [printCopy, setPrintCopy] = useState<'client' | 'internal'>('client');
  const effectiveCopy = canInternalCopy ? printCopy : 'client';

  const focusedApproval = useMemo(() => {
    if (docId && isEpcCaEntityType(focusEntityType)) {
      return { entityType: focusEntityType, entityId: docId };
    }
    // Infer from tab when bridge only passes caTab+docId
    if (!docId) return null;
    const byTab: Record<string, string> = {
      progress: 'epc_payment_application',
      variations: 'epc_variation',
      claims: 'epc_claim',
      eot: 'epc_eot',
      'final-account': 'epc_final_account',
      closeout: 'epc_contract_closeout',
    };
    const t = byTab[caTab];
    return t ? { entityType: t, entityId: docId } : null;
  }, [caTab, docId, focusEntityType]);

  const invalidateBilling = () => {
    qc.invalidateQueries({ queryKey: ['epc-contract-admin-pa', id] });
    qc.invalidateQueries({ queryKey: ['epc-contract-admin-certs', id] });
    qc.invalidateQueries({ queryKey: ['epc-contract-admin-workspace', id] });
  };

  const wsQ = useQuery({
    queryKey: ['epc-contract-admin-workspace', id],
    queryFn: () => epcContractAdminService.getWorkspace(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const noticesQ = useQuery({
    queryKey: ['epc-contract-admin-notices', id],
    queryFn: () => epcContractAdminService.listNotices(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const paQ = useQuery({
    queryKey: ['epc-contract-admin-pa', id],
    queryFn: () => epcContractAdminService.listPaymentApplications(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const certQ = useQuery({
    queryKey: ['epc-contract-admin-certs', id],
    queryFn: () => epcContractAdminService.listCertifications(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const voQ = useQuery({
    queryKey: ['epc-contract-admin-variations', id],
    queryFn: () => epcContractAdminService.listVariations(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const ceQ = useQuery({
    queryKey: ['epc-contract-admin-change-events', id],
    queryFn: () => epcContractAdminService.listChangeEvents(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const claimsQ = useQuery({
    queryKey: ['epc-contract-admin-claims', id],
    queryFn: () => epcContractAdminService.listClaims(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const eotQ = useQuery({
    queryKey: ['epc-contract-admin-eot', id],
    queryFn: () => epcContractAdminService.listEotRequests(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const forecastQ = useQuery({
    queryKey: ['epc-contract-admin-forecast', id],
    queryFn: () => epcContractAdminService.getForecast(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const faQ = useQuery({
    queryKey: ['epc-contract-admin-final-accounts', id],
    queryFn: () => epcContractAdminService.listFinalAccounts(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
  const closeQ = useQuery({
    queryKey: ['epc-contract-admin-closeouts', id],
    queryFn: () => epcContractAdminService.listCloseouts(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const projectId = String(
    (wsQ.data as { projectId?: string } | undefined)?.projectId ||
      (wsQ.data as { contract?: { project_id?: string } } | undefined)?.contract?.project_id ||
      ''
  );

  const finPosQ = useQuery({
    queryKey: ['project-financial-position', projectId],
    queryFn: () => projectService.getFinancialPosition(projectId),
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

  const submitPaM = useMutation({
    mutationFn: (paId: string) => epcContractAdminService.submitPaymentApplication(paId),
    onSuccess: () => {
      message.success('IPC submitted for approval');
      invalidateBilling();
    },
    onError: (e: unknown) =>
      message.error((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message || (e as Error)?.message || 'Submit failed'),
  });

  const createCertM = useMutation({
    mutationFn: (paymentApplicationId: string) =>
      epcContractAdminService.createCertification({ paymentApplicationId }),
    onSuccess: () => {
      message.success('Certification created');
      invalidateBilling();
    },
    onError: (e: unknown) =>
      message.error((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message || (e as Error)?.message || 'Create failed'),
  });

  const confirmCertM = useMutation({
    mutationFn: (certId: string) => epcContractAdminService.confirmCertification(certId),
    onSuccess: () => {
      message.success('Certification confirmed');
      invalidateBilling();
    },
    onError: (e: unknown) =>
      message.error((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message || (e as Error)?.message || 'Confirm failed'),
  });

  const convertSiM = useMutation({
    mutationFn: (args: { certId: string; departmentId: string }) =>
      epcContractAdminService.convertCertificationToSalesInvoice(args.certId, {
        departmentId: args.departmentId,
      }),
    onSuccess: (data) => {
      message.success('Draft Progress Tax Invoice ready');
      setConvertCertId(null);
      invalidateBilling();
      qc.invalidateQueries({ queryKey: ['project-financial-position', projectId] });
      const row = data as Record<string, unknown>;
      const bridge = row.salesInvoice as Record<string, unknown> | undefined;
      const cert = row.certification as Record<string, unknown> | undefined;
      const siId =
        bridge?.salesInvoiceId ||
        cert?.sales_invoice_id ||
        row.salesInvoiceId ||
        row.id;
      if (siId) navigate(`/growth/invoices/${String(siId)}`);
    },
    onError: (e: unknown) =>
      message.error((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message || (e as Error)?.message || 'Convert failed'),
  });

  if (!id) return null;

  if (wsQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (wsQ.isError || !wsQ.data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message={(wsQ.error as Error)?.message || 'Failed to load contract workspace'}
        />
      </div>
    );
  }

  const { contract, baseline, obligations, securities, insurance, milestones, calendar, diagnostics } =
    wsQ.data;
  const contractId = String(contract.id);
  const currency = contract.currency;
  const isDirect = String(contract.origin || '') === 'DIRECT' || !contract.award_id;
  const particulars =
    (contract.particulars as Record<string, unknown> | undefined) ||
    ({} as Record<string, unknown>);

  const items = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Contract #">
            {String(contract.contract_number || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Title">{String(contract.title || '—')}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag>{String(contract.status || '—')}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Clause profile">
            {String(contract.clauseProfile || contract.clause_profile || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Currency">{String(currency || '—')}</Descriptions.Item>
          <Descriptions.Item label="Commercial model">
            <Space wrap>
              <span>{normalizeCommercialModel(contract.commercial_model) || '—'}</span>
              <CommercialModelGuideLink current={contract.commercial_model as string | undefined} />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item
            label={commercialModelInfo(contract.commercial_model)?.valueIsEstimate ? 'Estimated contract sum' : 'Contract value'}
          >
            {money(contract.contract_value, currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Origin">
            {isDirect ? <Tag color="blue">Registered directly</Tag> : <Tag>From award</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Client contract ref">
            {String(contract.client_contract_ref || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Customer">
            {String(contract.customer_name || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Signed / commencement / completion">
            {[contract.signed_date, contract.commencement_date, contract.completion_date]
              .map((d) => (d ? String(d).slice(0, 10) : '—'))
              .join(' · ')}
          </Descriptions.Item>
          {!isDirect ? (
            <Descriptions.Item label="Award">
              {contract.award_id ? String(contract.award_id).slice(0, 8) : '—'}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Control tower">
            <Link to={`/epc/commercial/contracts/${contractId}/control`}>Open commercial control</Link>
          </Descriptions.Item>
        </Descriptions>
        <CommercialModelExplainer model={contract.commercial_model as string | undefined} compact />
        </Space>
      ),
    },
    {
      key: 'sov',
      label: 'Schedule of Values',
      children: <EpcSovEditor contractId={contractId} currency={String(currency || 'AED')} />,
    },
    {
      key: 'milestones',
      label: 'Payment Milestones',
      children: <EpcMilestonesEditor contractId={contractId} currency={String(currency || 'AED')} />,
    },
    ...(isDirect
      ? [
          {
            key: 'opening',
            label: 'Opening Position',
            children: (
              <EpcOpeningPositionForm contractId={contractId} currency={String(currency || 'AED')} />
            ),
          },
          {
            key: 'estimate',
            label: 'Estimate Basis',
            children: (
              <EpcContractEstimateBasisCard
                contractId={contractId}
                currency={String(currency || 'AED')}
              />
            ),
          },
        ]
      : []),
    {
      key: 'baseline',
      label: 'Commercial Baseline',
      children: baseline ? (
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Status">
            <Tag>{String(baseline.status || '—')}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Baseline #">
            {String(baseline.baseline_number || baseline.id || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Contract value">
            {money(baseline.contract_value ?? baseline.baseline_value, currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Currency">
            {String(baseline.currency || currency || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Frozen at">
            {baseline.frozen_at ? String(baseline.frozen_at).slice(0, 19) : '—'}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Alert type="warning" showIcon message="No commercial baseline on this contract" />
      ),
    },
    {
      key: 'particulars',
      label: 'Particulars',
      children: (
        <Descriptions bordered size="small" column={1}>
          {Object.keys(particulars).length === 0 ? (
            <Descriptions.Item label="Particulars">—</Descriptions.Item>
          ) : (
            Object.entries(particulars).map(([k, v]) => (
              <Descriptions.Item key={k} label={k}>
                {v == null || typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </Descriptions.Item>
            ))
          )}
          <Descriptions.Item label="Milestones">
            <SimpleTable
              rows={milestones}
              columns={[
                { title: 'No.', dataIndex: 'milestone_number', width: 100 },
                { title: 'Name', dataIndex: 'title' },
                { title: 'Planned', dataIndex: 'planned_date', width: 120 },
                { title: '%', dataIndex: 'percent', width: 80 },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  width: 140,
                  render: (v) => money(v, currency),
                },
                { title: 'Status', dataIndex: 'status', width: 120 },
              ]}
            />
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'obligations',
      label: 'Obligations',
      children: (
        <SimpleTable
          rows={obligations}
          columns={[
            { title: 'Title', dataIndex: 'title' },
            { title: 'Type', dataIndex: 'obligation_type', width: 140 },
            { title: 'Due', dataIndex: 'due_date', width: 120 },
            { title: 'Status', dataIndex: 'status', width: 120 },
            { title: 'Criticality', dataIndex: 'criticality', width: 110 },
          ]}
        />
      ),
    },
    {
      key: 'securities',
      label: 'Bonds & Guarantees',
      children: (
        <SimpleTable
          rows={securities}
          columns={[
            { title: 'Type', dataIndex: 'security_type', width: 140 },
            { title: 'Ref', dataIndex: 'reference_number', width: 140 },
            {
              title: 'Amount',
              dataIndex: 'amount',
              width: 140,
              render: (v, r) => money(v, r.currency || currency),
            },
            { title: 'Expiry', dataIndex: 'expiry_date', width: 120 },
            { title: 'Status', dataIndex: 'status', width: 120 },
          ]}
        />
      ),
    },
    {
      key: 'insurance',
      label: 'Insurance',
      children: (
        <SimpleTable
          rows={insurance}
          columns={[
            { title: 'Type', dataIndex: 'insurance_type', width: 140 },
            { title: 'Policy #', dataIndex: 'policy_number', width: 140 },
            { title: 'Insurer', dataIndex: 'insurer', width: 160 },
            { title: 'Expiry', dataIndex: 'expiry_date', width: 120 },
            { title: 'Status', dataIndex: 'status', width: 120 },
          ]}
        />
      ),
    },
    {
      key: 'calendar',
      label: 'Calendar',
      children: (
        <SimpleTable
          rows={calendar}
          columns={[
            { title: 'Date', dataIndex: 'date', width: 120 },
            { title: 'Kind', dataIndex: 'kind', width: 160 },
            { title: 'Title', dataIndex: 'title' },
          ]}
        />
      ),
    },
    {
      key: 'documents',
      label: 'Documents',
      children: <EntityAttachmentsCard entityType="EpcContract" entityId={contractId} />,
    },
    {
      key: 'approvals',
      label: 'Approvals',
      children: <ApprovalPanel entityType="epc_contract" entityId={contractId} />,
    },
    {
      key: 'diagnostics',
      label: 'Diagnostics',
      children: (
        <SimpleTable
          rows={diagnostics}
          columns={[
            {
              title: 'Severity',
              dataIndex: 'severity',
              width: 110,
              render: (v) => <Tag color={severityColor(v)}>{String(v || '—')}</Tag>,
            },
            { title: 'Code', dataIndex: 'code', width: 200 },
            { title: 'Message', dataIndex: 'message' },
          ]}
        />
      ),
    },
    {
      key: 'notices',
      label: 'Notices',
      children: noticesQ.isError ? (
        <Alert type="info" showIcon message="Coming in later wave" />
      ) : (
        <SimpleTable
          rows={(noticesQ.data || []) as Record<string, unknown>[]}
          columns={[
            { title: 'Number', dataIndex: 'notice_number', width: 140 },
            { title: 'Type', dataIndex: 'notice_type', width: 140 },
            { title: 'Subject', dataIndex: 'subject' },
            { title: 'Status', dataIndex: 'status', width: 120 },
            { title: 'Due', dataIndex: 'response_due_date', width: 120 },
          ]}
        />
      ),
    },
    {
      key: 'progress',
      label: 'Progress & Certification',
      children:
        paQ.isError && certQ.isError ? (
          <Alert type="info" showIcon message="Coming in later wave" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Space style={{ marginBottom: 8 }} wrap>
                <Text strong>Payment applications (IPC)</Text>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    navigate(`/epc/commercial/contracts/${contractId}/payment-applications/new`)
                  }
                >
                  New IPC
                </Button>
              </Space>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Create → submit for approval → create certification → confirm → convert to Draft
                Progress Tax Invoice (Billing tab).
              </Text>
              {paQ.isError ? (
                <Alert type="warning" showIcon message={(paQ.error as Error)?.message} />
              ) : (
                <SimpleTable
                  rows={(paQ.data || []) as Record<string, unknown>[]}
                  highlightId={caTab === 'progress' ? docId : undefined}
                  columns={[
                    {
                      title: 'Number',
                      dataIndex: 'application_number',
                      width: 140,
                      render: (v, r) => (
                        <Link
                          to={`/epc/commercial/contracts/${contractId}/payment-applications/${String(r.id)}`}
                        >
                          {String(v || r.id)}
                        </Link>
                      ),
                    },
                    {
                      title: 'Period',
                      dataIndex: 'period_from',
                      width: 180,
                      render: (_v, r) => {
                        const from = r.period_from ? String(r.period_from).slice(0, 10) : '';
                        const to = r.period_to ? String(r.period_to).slice(0, 10) : '';
                        if (!from && !to) return '—';
                        return `${from || '…'} — ${to || '…'}`;
                      },
                    },
                    {
                      title: 'Net applied',
                      dataIndex: 'net_applied_amount',
                      width: 140,
                      render: (v) => money(v, currency),
                    },
                    { title: 'Status', dataIndex: 'status', width: 120 },
                    {
                      title: 'Actions',
                      dataIndex: 'id',
                      width: 280,
                      render: (_v, r) => {
                        const st = String(r.status || '');
                        const hasCert = ((certQ.data || []) as Record<string, unknown>[]).some(
                          (c) => String(c.payment_application_id) === String(r.id)
                        );
                        return (
                          <Space size="small" wrap>
                            <Link
                              to={`/epc/commercial/contracts/${contractId}/payment-applications/${String(r.id)}`}
                            >
                              Open
                            </Link>
                            <Button
                              type="link"
                              size="small"
                              icon={<PrinterOutlined />}
                              onClick={() => void printIpcFromWorkspace(String(r.id))}
                            >
                              Print
                            </Button>
                            {st === 'Draft' || st === 'Rejected' ? (
                              <Button
                                type="link"
                                size="small"
                                loading={submitPaM.isPending}
                                onClick={() => submitPaM.mutate(String(r.id))}
                              >
                                Submit
                              </Button>
                            ) : null}
                            {['Approved', 'Certified'].includes(st) && !hasCert ? (
                              <Button
                                type="link"
                                size="small"
                                loading={createCertM.isPending}
                                onClick={() => createCertM.mutate(String(r.id))}
                              >
                                Create cert
                              </Button>
                            ) : null}
                          </Space>
                        );
                      },
                    },
                  ]}
                />
              )}
            </div>
            <div>
              <Text strong>Certifications</Text>
              {certQ.isError ? (
                <Alert type="warning" showIcon message={(certQ.error as Error)?.message} />
              ) : (
                <SimpleTable
                  rows={(certQ.data || []) as Record<string, unknown>[]}
                  columns={[
                    { title: 'Number', dataIndex: 'certification_number', width: 140 },
                    {
                      title: 'Certified net',
                      dataIndex: 'certified_net_amount',
                      width: 140,
                      render: (v) => money(v, currency),
                    },
                    { title: 'Status', dataIndex: 'status', width: 120 },
                    {
                      title: 'Tax invoice',
                      dataIndex: 'sales_invoice_id',
                      width: 180,
                      render: (_v, r) => {
                        const siId = r.sales_invoice_id ? String(r.sales_invoice_id) : '';
                        if (!siId) return '—';
                        const docNo =
                          String(r.sales_invoice_doc_number || '').trim() ||
                          `${siId.slice(0, 8)}…`;
                        const st = r.sales_invoice_status
                          ? String(r.sales_invoice_status)
                          : '';
                        return (
                          <Link to={`/growth/invoices/${siId}`}>
                            {docNo}
                            {st ? ` (${st})` : ''}
                          </Link>
                        );
                      },
                    },
                    {
                      title: 'Actions',
                      dataIndex: 'id',
                      width: 260,
                      render: (_v, r) => (
                        <Space size="small">
                          {String(r.status) !== 'Certified' ? (
                            <Button
                              type="link"
                              size="small"
                              loading={confirmCertM.isPending}
                              onClick={() => confirmCertM.mutate(String(r.id))}
                            >
                              Confirm
                            </Button>
                          ) : null}
                          {String(r.status) === 'Certified' && !r.sales_invoice_id ? (
                            <Button
                              type="link"
                              size="small"
                              loading={
                                convertSiM.isPending && convertCertId === String(r.id)
                              }
                              onClick={() => setConvertCertId(String(r.id))}
                            >
                              Convert SI
                            </Button>
                          ) : null}
                          {r.sales_invoice_id ? (
                            <Link to={`/growth/invoices/${String(r.sales_invoice_id)}`}>
                              Open SI
                            </Link>
                          ) : null}
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          </Space>
        ),
    },
    {
      key: 'billing',
      label: 'Billing',
      children: certQ.isError ? (
        <Alert type="info" showIcon message="Coming in later wave" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text type="secondary">
            Certification → Draft Progress Tax Invoice only (Sales/Finance own AR/GL). Submit →
            Approve → Post the SI, then allocate a Receipt Voucher.
          </Text>
          {projectId ? (
            <Card size="small" title="Project revenue & receivables">
              <Space size="large" wrap>
                <Statistic
                  title="Contract value"
                  value={Number(
                    (finPosQ.data as { revenue?: { contract_value?: number } })?.revenue
                      ?.contract_value ??
                      contract.contract_value ??
                      0
                  )}
                  precision={2}
                  prefix={currency ? String(currency) : undefined}
                />
                <Statistic
                  title="Certified"
                  value={Number(
                    (finPosQ.data as { revenue?: { certified?: number } })?.revenue?.certified ??
                      0
                  )}
                  precision={2}
                  prefix={currency ? String(currency) : undefined}
                />
                <Statistic
                  title="Billed"
                  value={Number(
                    (finPosQ.data as { revenue?: { billed?: number } })?.revenue?.billed ?? 0
                  )}
                  precision={2}
                  prefix={currency ? String(currency) : undefined}
                />
                <Statistic
                  title="Collected"
                  value={Number(
                    (finPosQ.data as { revenue?: { collected?: number } })?.revenue?.collected ??
                      0
                  )}
                  precision={2}
                  prefix={currency ? String(currency) : undefined}
                />
                <Statistic
                  title="AR outstanding"
                  value={Math.max(
                    0,
                    Number(
                      (finPosQ.data as { revenue?: { billed?: number } })?.revenue?.billed ?? 0
                    ) -
                      Number(
                        (finPosQ.data as { revenue?: { collected?: number } })?.revenue
                          ?.collected ?? 0
                      )
                  )}
                  precision={2}
                  prefix={currency ? String(currency) : undefined}
                />
              </Space>
              <Space style={{ marginTop: 12 }} wrap>
                <Link to={`/prj/projects/${projectId}/status-check`}>Project Status Check</Link>
                <Link to="/fin/receipt-vouchers">Receipt vouchers</Link>
                <Link to="/growth/invoices">Tax invoices</Link>
              </Space>
            </Card>
          ) : (
            <Alert
              type="info"
              showIcon
              message="No project linked yet"
              description="Link a project via mobilization to see billed / collected / AR on this contract."
            />
          )}
          <EpcAdvancePaymentCard
            contractId={contractId}
            currency={currency ? String(currency) : undefined}
            defaultDepartmentId={contract.department_id ? String(contract.department_id) : null}
            canRequest={
              !!user?.is_super_admin ||
              ['epc.certification.manage', 'epc.admin'].some((p) => user?.permissions?.includes(p))
            }
          />
          <SimpleTable
            rows={(certQ.data || []) as Record<string, unknown>[]}
            columns={[
              { title: 'Certification', dataIndex: 'certification_number', width: 160 },
              { title: 'Status', dataIndex: 'status', width: 120 },
              {
                title: 'Certified net',
                dataIndex: 'certified_net_amount',
                width: 140,
                render: (v) => money(v, currency),
              },
              {
                title: 'Retention',
                dataIndex: 'retention_withheld_amount',
                width: 120,
                render: (v) => money(v, currency),
              },
              {
                title: 'Tax invoice',
                dataIndex: 'sales_invoice_id',
                width: 180,
                render: (_v, r) => {
                  const siId = r.sales_invoice_id ? String(r.sales_invoice_id) : '';
                  if (!siId) return 'Not converted';
                  const docNo =
                    String(r.sales_invoice_doc_number || '').trim() || `${siId.slice(0, 8)}…`;
                  const st = r.sales_invoice_status ? String(r.sales_invoice_status) : '';
                  return (
                    <Link to={`/growth/invoices/${siId}`}>
                      {docNo}
                      {st ? ` (${st})` : ''}
                    </Link>
                  );
                },
              },
              {
                title: 'Actions',
                dataIndex: 'id',
                width: 220,
                render: (_v, r) => (
                  <Space size="small">
                    {String(r.status) === 'Certified' && !r.sales_invoice_id ? (
                      <Button
                        type="link"
                        size="small"
                        loading={convertSiM.isPending && convertCertId === String(r.id)}
                        onClick={() => setConvertCertId(String(r.id))}
                      >
                        Convert SI
                      </Button>
                    ) : null}
                    {r.sales_invoice_id ? (
                      <Link to={`/growth/invoices/${String(r.sales_invoice_id)}`}>Open SI</Link>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Space>
      ),
    },
    {
      key: 'variations',
      label: 'Variations',
      children:
        voQ.isError && ceQ.isError ? (
          <Alert type="info" showIcon message="Coming in later wave" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong>Change events</Text>
              {ceQ.isError ? (
                <Alert type="warning" showIcon message={(ceQ.error as Error)?.message} />
              ) : (
                <SimpleTable
                  rows={(ceQ.data || []) as Record<string, unknown>[]}
                  columns={[
                    { title: 'Number', dataIndex: 'change_event_number', width: 140 },
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Status', dataIndex: 'status', width: 120 },
                  ]}
                />
              )}
            </div>
            <div>
              <Text strong>Variations</Text>
              {voQ.isError ? (
                <Alert type="warning" showIcon message={(voQ.error as Error)?.message} />
              ) : (
                <SimpleTable
                  rows={(voQ.data || []) as Record<string, unknown>[]}
                  highlightId={caTab === 'variations' ? docId : undefined}
                  columns={[
                    { title: 'Number', dataIndex: 'variation_number', width: 140 },
                    { title: 'Title', dataIndex: 'title' },
                    {
                      title: 'Proposed',
                      dataIndex: 'proposed_value',
                      width: 140,
                      render: (v) => money(v, currency),
                    },
                    { title: 'Status', dataIndex: 'status', width: 120 },
                  ]}
                />
              )}
            </div>
          </Space>
        ),
    },
    {
      key: 'claims',
      label: 'Claims',
      children: claimsQ.isError ? (
        <Alert type="info" showIcon message="Coming in later wave" />
      ) : (
        <SimpleTable
          rows={(claimsQ.data || []) as Record<string, unknown>[]}
          highlightId={caTab === 'claims' ? docId : undefined}
          columns={[
            { title: 'Number', dataIndex: 'claim_number', width: 140 },
            { title: 'Title', dataIndex: 'title' },
            {
              title: 'Claimed',
              dataIndex: 'claimed_amount',
              width: 140,
              render: (v) => money(v, currency),
            },
            { title: 'Status', dataIndex: 'status', width: 120 },
          ]}
        />
      ),
    },
    {
      key: 'eot',
      label: 'EOT',
      children: eotQ.isError ? (
        <Alert type="info" showIcon message="Coming in later wave" />
      ) : (
        <SimpleTable
          rows={(eotQ.data || []) as Record<string, unknown>[]}
          highlightId={caTab === 'eot' ? docId : undefined}
          columns={[
            { title: 'Number', dataIndex: 'eot_number', width: 140 },
            { title: 'Title', dataIndex: 'title' },
            { title: 'Days claimed', dataIndex: 'days_claimed', width: 120 },
            { title: 'Status', dataIndex: 'status', width: 120 },
          ]}
        />
      ),
    },
    {
      key: 'forecast',
      label: 'Commercial Forecast',
      children: forecastQ.isError ? (
        <Alert type="info" showIcon message="Coming in later wave" />
      ) : forecastQ.isLoading || !forecastQ.data ? (
        <Spin />
      ) : (
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Contract value">
            {money(forecastQ.data.contractValue, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Approved variations">
            {money(forecastQ.data.approvedVariations, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Expected approved">
            {money(forecastQ.data.expectedApprovedValue, forecastQ.data.currency)}
          </Descriptions.Item>
          {forecastQ.data.certifiedGrossToDate != null ? (
            <Descriptions.Item label="Certified to date (gross)">
              {money(forecastQ.data.certifiedGrossToDate, forecastQ.data.currency)}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label="Certified to date (net)">
            {money(forecastQ.data.certifiedToDate, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Applied not certified">
            {money(forecastQ.data.appliedNotCertified, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Claims open">
            {money(forecastQ.data.claimsOpen, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Retention held">
            {money(forecastQ.data.retentionHeld, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="LD exposure (advisory)">
            {money(forecastQ.data.ldExposureAdvisory, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="Projected final">
            {money(forecastQ.data.projectedFinal, forecastQ.data.currency)}
          </Descriptions.Item>
          <Descriptions.Item label="As of">
            {String(forecastQ.data.asOf || '').slice(0, 19) || '—'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'final-account',
      label: 'Final Account',
      children: faQ.isError ? (
        <Alert type="info" showIcon message="Coming in later wave" />
      ) : (
        <SimpleTable
          rows={(faQ.data || []) as Record<string, unknown>[]}
          highlightId={caTab === 'final-account' ? docId : undefined}
          columns={[
            { title: 'Number', dataIndex: 'final_account_number', width: 160 },
            { title: 'Status', dataIndex: 'status', width: 120 },
            {
              title: 'Agreed value',
              dataIndex: 'agreed_final_value',
              width: 140,
              render: (v) => money(v, currency),
            },
          ]}
        />
      ),
    },
    {
      key: 'closeout',
      label: 'Closeout',
      children: closeQ.isError ? (
        <Alert type="info" showIcon message="Coming in later wave" />
      ) : (
        <SimpleTable
          rows={(closeQ.data || []) as Record<string, unknown>[]}
          highlightId={caTab === 'closeout' ? docId : undefined}
          columns={[
            { title: 'Number', dataIndex: 'closeout_number', width: 160 },
            { title: 'Status', dataIndex: 'status', width: 120 },
            { title: 'Notes', dataIndex: 'notes' },
          ]}
        />
      ),
    },
  ];

  const contractStatus = String(contract.status || '');
  const showMasterCockpit = ['Draft', 'Rejected', 'Submitted', 'Approved'].includes(contractStatus);


  const printIpcFromWorkspace = async (applicationId: string) => {
    try {
      const application = await epcContractAdminService.getPaymentApplication(applicationId);
      const contract = (wsQ.data as Record<string, unknown> | undefined)?.contract as
        | Record<string, unknown>
        | undefined;
      openPmvPrintOverlay((onClose) => (
        <PrintableIpcPaymentApplication
          application={application}
          contract={contract}
          customerName={
            contract?.customerName
              ? String(contract.customerName)
              : contract?.customer_name
                ? String(contract.customer_name)
                : undefined
          }
          projectLabel={
            contract?.projectLabel
              ? String(contract.projectLabel)
              : contract?.project_label
                ? String(contract.project_label)
                : undefined
          }
          onClose={onClose}
        />
      ));
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to open IPC print');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/epc/commercial/contracts')}>
          Contracts
        </Button>
        <Button
          icon={<ControlOutlined />}
          onClick={() => navigate(`/epc/commercial/contracts/${contractId}/control`)}
        >
          Control tower
        </Button>
        {canInternalCopy ? (
          <Segmented
            value={effectiveCopy}
            onChange={(v) => setPrintCopy(v as 'client' | 'internal')}
            options={[
              { label: 'Client copy', value: 'client' },
              { label: 'Internal copy', value: 'internal' },
            ]}
          />
        ) : null}
        <ReportDocumentActions
          reportKey="epc-contract"
          params={{ id: contractId, copy: effectiveCopy }}
          documentId={contractId}
          sendPermissions={['epc.contract.manage', 'epc.admin']}
        />
      </Space>
      <Title level={3} style={{ marginBottom: 4 }}>
        {String(contract.title || 'Contract administration')}{' '}
        <Text type="secondary" style={{ fontSize: 16 }}>
          {String(contract.contract_number || '')}
        </Text>
      </Title>
      <Tag style={{ marginBottom: 16 }}>{contractStatus}</Tag>

      {showMasterCockpit ? (
        <div style={{ marginBottom: 24 }}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Complete contract master before CA execution"
            description="Draft contracts are not frozen. Fill commercial particulars, submit for approval, then activate. CA tabs below become the operating surface after Active."
          />
          <EpcContractCockpit
            contract={contract as Record<string, unknown>}
            onUpdated={() => {
              wsQ.refetch();
            }}
          />
        </div>
      ) : null}

      {focusedApproval ? (
        <div style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Opened from approval / notification"
            description={`Source ${focusedApproval.entityType} · ${focusedApproval.entityId.slice(0, 8)}…`}
          />
          <ApprovalPanel
            entityType={focusedApproval.entityType}
            entityId={focusedApproval.entityId}
            extraInvalidateQueryKeys={[
              ['epc-contract-admin-pa', contractId],
              ['epc-contract-admin-variations', contractId],
              ['epc-contract-admin-claims', contractId],
              ['epc-contract-admin-eot', contractId],
              ['epc-contract-admin-final-accounts', contractId],
              ['epc-contract-admin-closeouts', contractId],
            ]}
          />
        </div>
      ) : null}

      <Tabs
        activeKey={caTab}
        onChange={(key) => {
          const next = new URLSearchParams(searchParams);
          next.set('caTab', key);
          setSearchParams(next, { replace: true });
        }}
        items={items}
      />

      <ConvertCertificationToSalesInvoiceModal
        open={Boolean(convertCertId)}
        confirming={convertSiM.isPending}
        defaultDepartmentId={
          contract.department_id ? String(contract.department_id) : null
        }
        onCancel={() => setConvertCertId(null)}
        onConfirm={(payload) => {
          if (!convertCertId) return;
          convertSiM.mutate({ certId: convertCertId, ...payload });
        }}
      />
    </div>
  );
};

export default EpcContractAdminWorkspacePage;
